import { ReactNode, useLayoutEffect, useState } from 'react';
import { getProject, useProject } from '@client/store/project/project.hook';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { ScopeState } from '@shared/models/scope-selection.model';
import { bindProjectScope, overviewScopeStore } from './scope.store';
import {
  getProjectAppliedScope,
  setProjectAppliedScope,
  getOverviewAppliedScope,
} from './scope-migration.helpers';
import { getEnabledScopeIds } from '@features/lifecycles/saved-scopes.helpers';
import { useSavedScopes } from '@features/lifecycles/saved-scopes.hooks';
import { useOrganizationScopes } from '@features/lifecycles/organization-scopes.hooks';

/** Activate the loaded route's selection before rendering its result consumers. */
export const ScopeContextSync = ({
  overview,
  children,
}: {
  overview: boolean;
  children: ReactNode;
}) => {
  const project = useProject();
  const readonly = useIsReadonly();
  const organizationScopes = useOrganizationScopes();
  const { scopes: savedScopes, organization } = useSavedScopes();
  const contextKey = overview ? 'overview' : `project:${project.id}`;
  const [readyContext, setReadyContext] = useState<string>();
  useLayoutEffect(() => {
    if (overview) {
      // Apply fallback logic to overview scope
      const enabledIds = organizationScopes.enabledIds;
      const stored = overviewScopeStore.get();
      const scope = getOverviewAppliedScope(enabledIds, stored);
      // Persist the scope with fallback applied
      overviewScopeStore.set(scope);
      setReadyContext(contextKey);
      return;
    }
    const enabledIds = getEnabledScopeIds(project.meta);
    const scope = getProjectAppliedScope(
      project.id,
      enabledIds,
      savedScopes,
      organization,
    );
    // Persist the initial scope (including fallback) in non-readonly mode
    if (!readonly) {
      setProjectAppliedScope(project.id, scope);
    }
    const release = bindProjectScope({
      id: project.id,
      scope,
      onChange: (newScope: ScopeState) => {
        if (readonly || getProject().id !== project.id) return;
        // Scope selection is a local view preference, saved per user per project.
        setProjectAppliedScope(project.id, newScope);
      },
    });
    setReadyContext(contextKey);
    return release;
  }, [
    overview,
    contextKey,
    project.id,
    project.meta,
    readonly,
    organizationScopes.enabledIds,
    savedScopes,
    organization,
  ]);
  return readyContext === contextKey ? children : null;
};
